import React, { useContext, useEffect, useState } from "react";
import {
  AccessTimeRounded,
  CheckCircleRounded,
  GridViewRounded,
  TimelineRounded,
  WarningAmberRounded,
} from "@mui/icons-material";
import { Box, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AppVarsContext } from "../../context/AppVarsContext";
import GlassPanel from "../../components/glass/GlassPanel";

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const parseDate = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const normalized = /(?:Z|[+-]\d{2}:\d{2})$/.test(value) ? value : `${value}Z`;
  const parsed = new Date(normalized);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isWithinLast24Hours = (dateInput) => {
  const parsedDate = parseDate(dateInput);

  if (!parsedDate) {
    return false;
  }

  return Date.now() - parsedDate.getTime() < DAY_IN_MS;
};

const daysSince = (dateInput) => {
  const parsedDate = parseDate(dateInput);

  if (!parsedDate) {
    return null;
  }

  return Math.floor((Date.now() - parsedDate.getTime()) / DAY_IN_MS);
};

const formatLastFire = (dateInput) => {
  const parsedDate = parseDate(dateInput);

  if (!parsedDate) {
    return "No recent activity";
  }

  return parsedDate
    .toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
    .replace(",", "");
};

const formatFreshness = (dateInput) => {
  const dayCount = daysSince(dateInput);

  if (dayCount === null) {
    return "No data";
  }

  if (dayCount <= 0) {
    return "Today";
  }

  if (dayCount === 1) {
    return "1 day";
  }

  return `${dayCount} days`;
};

const getInitials = (value = "") =>
  value
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((entry) => entry[0]?.toUpperCase())
    .join("") || "DW";

const MetricCard = ({ icon, label, value, caption, tint, appAppearance }) => (
  <GlassPanel
    sx={{
      p: 2,
      height: "100%",
      background: `linear-gradient(160deg, ${alpha("#ffffff", 0.78)} 0%, ${alpha(tint, 0.08)} 100%)`,
      boxShadow: "none",
    }}
  >
    <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          variant="overline"
          sx={{
            color: appAppearance.colors.textMuted,
            letterSpacing: "0.14em",
            fontWeight: 700,
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: alpha(tint, 0.12),
            color: tint,
            border: `1px solid ${alpha(tint, 0.16)}`,
          }}
        >
          {icon}
        </Box>
      </Stack>
      <Typography
        variant="h4"
        sx={{
          color: appAppearance.colors.textPrimary,
          fontWeight: 800,
          letterSpacing: "-0.04em",
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: appAppearance.colors.textSecondary,
          lineHeight: 1.6,
        }}
      >
        {caption}
      </Typography>
    </Stack>
  </GlassPanel>
);

const EcommerceStatus = ({ clientID }) => {
  const [ecom, setEcom] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { appVars, appAppearance } = useContext(AppVarsContext);

  useEffect(() => {
    if (!appVars?.api_url || !clientID) {
      return undefined;
    }

    let isActive = true;

    const fetchData = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await fetch(`${appVars.api_url}/platform/status`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ cid: clientID }),
          redirect: "follow",
        });

        if (!res.ok) {
          throw new Error("Unable to load platform status.");
        }

        const response = await res.json();

        if (isActive) {
          setEcom(Array.isArray(response) ? response : []);
        }
      } catch (fetchError) {
        if (isActive) {
          setEcom([]);
          setError(fetchError.message || "Unable to load platform status.");
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isActive = false;
    };
  }, [appVars?.api_url, clientID]);

  const healthyCount = ecom.filter((entry) => isWithinLast24Hours(entry.lasttrack)).length;
  const staleCount = ecom.length - healthyCount;
  const mostRecentTrack = ecom.reduce((latest, entry) => {
    const parsedDate = parseDate(entry.lasttrack);

    if (!parsedDate) {
      return latest;
    }

    if (!latest || parsedDate > latest) {
      return parsedDate;
    }

    return latest;
  }, null);

  return (
    <GlassPanel sx={{ p: { xs: 2, md: 3 }, display: "grid", gap: 2.5 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "flex-end" }}
        justifyContent="space-between"
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              color: appAppearance.colors.textMuted,
              letterSpacing: "0.16em",
              fontWeight: 700,
            }}
          >
            Commerce Monitoring
          </Typography>
          <Typography
            variant="h4"
            sx={{
              color: appAppearance.colors.textPrimary,
              fontWeight: 800,
              letterSpacing: "-0.04em",
            }}
          >
            Ecommerce Tracking Status
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mt: 0.75,
              maxWidth: 760,
              color: appAppearance.colors.textSecondary,
              lineHeight: 1.7,
            }}
          >
            Monitor recent firing activity across connected systems and see
            what is healthy, what needs attention, and when each property last
            reported.
          </Typography>
        </Box>

        <Chip
          label={
            loading
              ? "Refreshing platform data"
              : `${healthyCount} of ${ecom.length || 0} reporting in the last 24 hours`
          }
          sx={{
            height: 36,
            borderRadius: "999px",
            bgcolor: alpha("#ffffff", 0.58),
            border: `1px solid ${appAppearance.colors.border}`,
            "& .MuiChip-label": {
              px: 1.6,
              fontWeight: 700,
              color: appAppearance.colors.textPrimary,
            },
          }}
        />
      </Stack>

      {loading ? (
        <GlassPanel
          sx={{
            p: { xs: 3, md: 4 },
            minHeight: 280,
            display: "grid",
            placeItems: "center",
            boxShadow: "none",
          }}
        >
          <Stack spacing={1.5} alignItems="center">
            <CircularProgress size={28} />
            <Typography variant="body2" sx={{ color: appAppearance.colors.textSecondary }}>
              Loading the latest ecommerce activity...
            </Typography>
          </Stack>
        </GlassPanel>
      ) : error ? (
        <GlassPanel
          sx={{
            p: { xs: 3, md: 4 },
            background: `linear-gradient(160deg, ${alpha("#ffffff", 0.78)} 0%, ${alpha(appAppearance.colors.danger, 0.06)} 100%)`,
            boxShadow: "none",
          }}
        >
          <Stack spacing={1}>
            <Typography
              variant="h6"
              sx={{ color: appAppearance.colors.textPrimary, fontWeight: 700 }}
            >
              Status data unavailable
            </Typography>
            <Typography variant="body2" sx={{ color: appAppearance.colors.textSecondary }}>
              {error}
            </Typography>
          </Stack>
        </GlassPanel>
      ) : ecom.length === 0 ? (
        <GlassPanel
          sx={{
            p: { xs: 3, md: 4 },
            background: `linear-gradient(160deg, ${alpha("#ffffff", 0.78)} 0%, ${alpha(appAppearance.colors.secondary, 0.06)} 100%)`,
            boxShadow: "none",
          }}
        >
          <Stack spacing={1}>
            <Typography
              variant="h6"
              sx={{ color: appAppearance.colors.textPrimary, fontWeight: 700 }}
            >
              No ecommerce systems are currently connected
            </Typography>
            <Typography variant="body2" sx={{ color: appAppearance.colors.textSecondary }}>
              Once systems begin reporting, they will appear here with their
              last-fire timestamps and health status.
            </Typography>
          </Stack>
        </GlassPanel>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                xl: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.5,
            }}
          >
            <MetricCard
              icon={<GridViewRounded fontSize="small" />}
              label="Systems"
              value={ecom.length}
              caption="Connected properties currently being monitored."
              tint={appAppearance.colors.primary}
              appAppearance={appAppearance}
            />
            <MetricCard
              icon={<CheckCircleRounded fontSize="small" />}
              label="Healthy"
              value={healthyCount}
              caption="Properties that have fired within the last 24 hours."
              tint={appAppearance.colors.success}
              appAppearance={appAppearance}
            />
            <MetricCard
              icon={<WarningAmberRounded fontSize="small" />}
              label="Needs Review"
              value={staleCount}
              caption="Properties that have not fired within the expected reporting window."
              tint={appAppearance.colors.danger}
              appAppearance={appAppearance}
            />
            <MetricCard
              icon={<TimelineRounded fontSize="small" />}
              label="Latest Fire"
              value={mostRecentTrack ? formatFreshness(mostRecentTrack) : "No data"}
              caption={
                mostRecentTrack
                  ? formatLastFire(mostRecentTrack)
                  : "No recent tracking activity available."
              }
              tint={appAppearance.colors.secondary}
              appAppearance={appAppearance}
            />
          </Box>

          <GlassPanel sx={{ p: { xs: 1.25, md: 1.5 }, boxShadow: "none" }}>
            <Box
              sx={{
                display: { xs: "none", md: "grid" },
                gridTemplateColumns: "minmax(280px, 1.8fr) 0.8fr 1fr 0.8fr",
                gap: 2,
                px: 1.5,
                py: 1.25,
                borderRadius: 3,
                border: `1px solid ${appAppearance.colors.divider}`,
                background: `linear-gradient(135deg, ${alpha(appAppearance.colors.primary, 0.1)} 0%, ${alpha(appAppearance.colors.secondary, 0.08)} 100%)`,
              }}
            >
              {["System", "Freshness", "Last Fire", "24h Status"].map((label) => (
                <Typography
                  key={label}
                  variant="overline"
                  sx={{
                    color: appAppearance.colors.textMuted,
                    letterSpacing: "0.14em",
                    fontWeight: 700,
                  }}
                >
                  {label}
                </Typography>
              ))}
            </Box>

            <Box
              sx={{
                mt: { xs: 0, md: 1.25 },
                display: { xs: "none", md: "grid" },
                gap: 1,
              }}
            >
              {ecom.map((entry, index) => {
                const healthy = isWithinLast24Hours(entry.lasttrack);

                return (
                  <Box
                    key={`${entry.pkRecID || entry.site}-${index}`}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "minmax(280px, 1.8fr) 0.8fr 1fr 0.8fr",
                      gap: 2,
                      alignItems: "center",
                      px: 1.5,
                      py: 1.25,
                      borderRadius: 3,
                      border: `1px solid ${appAppearance.colors.divider}`,
                      background:
                        index % 2 === 0
                          ? alpha("#ffffff", 0.52)
                          : alpha(appAppearance.colors.primary, 0.035),
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 2.5,
                          position: "relative",
                          overflow: "hidden",
                          flexShrink: 0,
                          display: "grid",
                          placeItems: "center",
                          border: `1px solid ${appAppearance.colors.border}`,
                          background: `linear-gradient(180deg, ${alpha("#ffffff", 0.9)} 0%, ${alpha(appAppearance.colors.primary, 0.08)} 100%)`,
                        }}
                      >
                        <Typography
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "grid",
                            placeItems: "center",
                            color: appAppearance.colors.primary,
                            fontWeight: 800,
                            letterSpacing: "-0.03em",
                          }}
                        >
                          {getInitials(entry.site)}
                        </Typography>
                        <Box
                          component="img"
                          src={`${appVars.api_url}/imageFiles/${entry.pkRecID}.png`}
                          alt={`${entry.site} logo`}
                          sx={{
                            position: "relative",
                            zIndex: 1,
                            width: "72%",
                            height: "72%",
                            objectFit: "contain",
                          }}
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            color: appAppearance.colors.textPrimary,
                            fontWeight: 700,
                          }}
                        >
                          {entry.site}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: appAppearance.colors.textSecondary }}
                        >
                          Recent activity window and firing health for this
                          property.
                        </Typography>
                      </Box>
                    </Stack>

                    <Chip
                      label={formatFreshness(entry.lasttrack)}
                      sx={{
                        width: "fit-content",
                        borderRadius: "999px",
                        bgcolor: alpha("#ffffff", 0.6),
                        border: `1px solid ${appAppearance.colors.border}`,
                        "& .MuiChip-label": {
                          px: 1.4,
                          fontWeight: 700,
                          color: appAppearance.colors.textPrimary,
                        },
                      }}
                    />

                    <Stack direction="row" spacing={1} alignItems="center">
                      <AccessTimeRounded
                        sx={{
                          fontSize: 18,
                          color: appAppearance.colors.textMuted,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: appAppearance.colors.textSecondary }}
                      >
                        {formatLastFire(entry.lasttrack)}
                      </Typography>
                    </Stack>

                    <Chip
                      icon={
                        healthy ? (
                          <CheckCircleRounded sx={{ color: appAppearance.colors.success }} />
                        ) : (
                          <WarningAmberRounded sx={{ color: appAppearance.colors.danger }} />
                        )
                      }
                      label={healthy ? "Healthy" : "Needs review"}
                      sx={{
                        width: "fit-content",
                        borderRadius: "999px",
                        bgcolor: alpha("#ffffff", 0.6),
                        border: `1px solid ${healthy ? alpha(appAppearance.colors.success, 0.22) : alpha(appAppearance.colors.danger, 0.22)}`,
                        "& .MuiChip-label": {
                          px: 1.25,
                          fontWeight: 700,
                          color: appAppearance.colors.textPrimary,
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>

            <Box
              sx={{
                display: { xs: "grid", md: "none" },
                gap: 1.25,
              }}
            >
              {ecom.map((entry, index) => {
                const healthy = isWithinLast24Hours(entry.lasttrack);

                return (
                  <GlassPanel
                    key={`${entry.pkRecID || entry.site}-${index}-mobile`}
                    sx={{
                      p: 1.5,
                      boxShadow: "none",
                      background:
                        index % 2 === 0
                          ? `linear-gradient(180deg, ${alpha("#ffffff", 0.82)} 0%, ${alpha("#ffffff", 0.7)} 100%)`
                          : `linear-gradient(180deg, ${alpha("#ffffff", 0.78)} 0%, ${alpha(appAppearance.colors.primary, 0.05)} 100%)`,
                    }}
                  >
                    <Stack spacing={1.25}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: 2.5,
                            position: "relative",
                            overflow: "hidden",
                            flexShrink: 0,
                            display: "grid",
                            placeItems: "center",
                            border: `1px solid ${appAppearance.colors.border}`,
                            background: `linear-gradient(180deg, ${alpha("#ffffff", 0.9)} 0%, ${alpha(appAppearance.colors.primary, 0.08)} 100%)`,
                          }}
                        >
                          <Typography
                            sx={{
                              position: "absolute",
                              inset: 0,
                              display: "grid",
                              placeItems: "center",
                              color: appAppearance.colors.primary,
                              fontWeight: 800,
                              letterSpacing: "-0.03em",
                            }}
                          >
                            {getInitials(entry.site)}
                          </Typography>
                          <Box
                            component="img"
                            src={`${appVars.api_url}/imageFiles/${entry.pkRecID}.png`}
                            alt={`${entry.site} logo`}
                            sx={{
                              position: "relative",
                              zIndex: 1,
                              width: "72%",
                              height: "72%",
                              objectFit: "contain",
                            }}
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                        </Box>
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{
                              color: appAppearance.colors.textPrimary,
                              fontWeight: 700,
                            }}
                          >
                            {entry.site}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: appAppearance.colors.textSecondary }}
                          >
                            Last fire {formatLastFire(entry.lasttrack)}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        <Chip
                          label={formatFreshness(entry.lasttrack)}
                          sx={{
                            borderRadius: "999px",
                            bgcolor: alpha("#ffffff", 0.6),
                            border: `1px solid ${appAppearance.colors.border}`,
                            "& .MuiChip-label": {
                              px: 1.25,
                              fontWeight: 700,
                              color: appAppearance.colors.textPrimary,
                            },
                          }}
                        />
                        <Chip
                          icon={
                            healthy ? (
                              <CheckCircleRounded sx={{ color: appAppearance.colors.success }} />
                            ) : (
                              <WarningAmberRounded sx={{ color: appAppearance.colors.danger }} />
                            )
                          }
                          label={healthy ? "Healthy" : "Needs review"}
                          sx={{
                            borderRadius: "999px",
                            bgcolor: alpha("#ffffff", 0.6),
                            border: `1px solid ${healthy ? alpha(appAppearance.colors.success, 0.22) : alpha(appAppearance.colors.danger, 0.22)}`,
                            "& .MuiChip-label": {
                              px: 1.25,
                              fontWeight: 700,
                              color: appAppearance.colors.textPrimary,
                            },
                          }}
                        />
                      </Stack>
                    </Stack>
                  </GlassPanel>
                );
              })}
            </Box>
          </GlassPanel>
        </>
      )}
    </GlassPanel>
  );
};

export default EcommerceStatus;
